import React, { useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AppRouteURI } from 'router/RoleBasedAccess';
import useStyles from './Styles';
import { Typography, Container } from '@mui/material'
import AppActions from 'components/app/actions'
import authActions from '../actions'
import { useHistory } from 'react-router'
import CopyrightFooter from 'components/common/CopyrightFooter';
import { USER_REGISTER_URL } from 'api/AuthConstants';
import { LOAD_INVITE, PROCESS_INVITE } from 'api/InvitesConstants';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Alert, AlertTitle, InputAdornment, IconButton } from '@mui/material';
import UnstyledLink from 'components/common/UnstyledLink';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const validationSchema = yup.object({
    firstname: yup
        .string('Enter your first name')
        .min(2, 'First name should be of minimum 2 characters length')
        .required('First name is required'),
    lastname: yup
        .string('Enter your last name')
        .min(2, 'Last name should be of minimum 2 characters length')
        .required('Last name is required'),
    email: yup
        .string('Enter your email')
        .email('Enter a valid email')
        .required('Email is required'),
    affiliation: yup
        .string('Enter your affiliation')
        .required('Affiliation is required'),
    title: yup
        .string('Enter your title')
        .required('Title is required'),
    password: yup
        .string('Enter your password')
        .min(8, 'Password should be of minimum 8 characters length')
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/, 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character')
        .required('Password is required'),
    confirmPassword: yup
        .string('Confirm your password')
        .oneOf([yup.ref('password'), null], 'Passwords must match')
        .required('Confirm Password is required'),
    terms: yup
        .boolean()
        .oneOf([true], 'You must accept the terms and conditions')
});

export default () => {
    const classes = useStyles();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [affiliationOptions, setAffiliationOptions] = useState([]);
    const { setToastMessage } = AppActions()
    const { setPendingEmailVerificationEmail } = authActions()
    const history = useHistory()
    const formik = useFormik({
        initialValues: {
            firstname: '',
            lastname: '',
            email: '',
            affiliation: '',
            title: '',
            password: '',
            confirmPassword: '',
            terms: false
        },
        validationSchema: validationSchema,
        onSubmit: (values) => submitForm(values)
    });
    const [formErrors, setFormErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isInvitation, setIsInvitation] = useState(false);
    const [inviteToken, setInviteToken] = useState(null);

    useEffect(() => {
        const token = new URLSearchParams(window.location.search).get('token');
        if (!token) {
            setToastMessage({
                message: "Invitation invalid. Please contact the admin for a new invite.",
                type: 'error'
            });
            history.push(AppRouteURI.LOGIN);
        }
        setInviteToken(token);
        if (token) {
            fetch(`${LOAD_INVITE}/${token}`)
                .then(res => res.json())
                .then(data => {
                    if (data.data.email) {
                        formik.setValues({
                            ...formik.values,
                            email: data.data.email,
                        });
                        setIsInvitation(true);
                        setToastMessage({
                            message: "Please fill in the remaining fields",
                            type: 'info'
                        });
                    }
                    else {
                        throw new Error(data.message);
                    }
                })
                .catch(err => {
                    console.error(err);
                    setToastMessage({
                        message: "Invitation invalid. Please contact the admin for a new invite.",
                        type: 'error'
                    });
                    history.push(AppRouteURI.LOGIN);
                });
        }
    }, []);

    const submitForm = (values) => {
        setLoading(true);
        const url = isInvitation ? PROCESS_INVITE : USER_REGISTER_URL;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                affiliation: values.affiliation,
                title: values.title,
                firstname: values.firstname,
                lastname: values.lastname,
                email: values.email.toLowerCase(),
                password: values.password,
                token: inviteToken
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(res => res.json())
            .then(data => {
                if (data.user_id) {
                    setToastMessage({
                        message: data.status_message,
                        type: data.success ? 'success' : 'warning'
                    });
                    if (data.success) {
                        history.push(AppRouteURI.LOGIN);
                    }
                } else {
                    if (data.errors) {
                        setFormErrors(data.errors);
                        throw new Error(data.message);
                    } else {
                        throw new Error(data.message);
                    }
                }
            })
            .catch(err => {
                console.log(err);
                setToastMessage({
                    message: err.message,
                    type: 'error'
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <div className={classes.paper}>
                <Avatar className={classes.avatar}>
                    <LockOutlinedIcon style={{ color: 'black' }} />
                </Avatar>
                <Typography component="h1" variant="h5" color="textPrimary">
                    Sign up
                </Typography>
                {formErrors && Object.keys(formErrors).length > 0 && (
                    <Alert severity="error" style={{ marginTop: '1rem' }}>
                        <AlertTitle>Error</AlertTitle>
                        {Object.entries(formErrors).map(([field, message]) => (
                            <div key={field}>{message}</div>
                        ))}
                    </Alert>
                )}
                <form className={classes.form} onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoComplete="fname"
                                name="firstname"
                                variant="outlined"
                                fullWidth
                                id="firstname"
                                label="First Name"
                                autoFocus
                                value={formik.values.firstname}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.firstname && Boolean(formik.errors.firstname)}
                                helperText={formik.touched.firstname && formik.errors.firstname}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                id="lastname"
                                label="Last Name"
                                name="lastname"
                                autoComplete="lname"
                                value={formik.values.lastname}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.lastname && Boolean(formik.errors.lastname)}
                                helperText={formik.touched.lastname && formik.errors.lastname}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={isInvitation}
                                variant="outlined"
                                fullWidth
                                id="email"
                                label="Email Address"
                                name="email"
                                autoComplete="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                id="affiliation"
                                label="Affiliation"
                                name="affiliation"
                                autoComplete="affiliation"
                                value={formik.values.affiliation}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.affiliation && Boolean(formik.errors.affiliation)}
                                helperText={formik.touched.affiliation && formik.errors.affiliation}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                id="title"
                                label="Title"
                                name="title"
                                autoComplete="title"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.title && Boolean(formik.errors.title)}
                                helperText={formik.touched.title && formik.errors.title}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                edge="end"
                                                aria-label="toggle password visibility"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                name="confirmPassword"
                                label="Confirm Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                autoComplete="current-password"
                                value={formik.values.confirmPassword}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                edge="end"
                                                aria-label="toggle confirm password visibility"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox color="primary" value={formik.values.terms} onChange={formik.handleChange} name="terms" />}
                                label="I accept the terms and conditions"
                            />
                            {formik.touched.terms && Boolean(formik.errors.terms) &&
                                <FormHelperText error>{formik.errors.terms}</FormHelperText>
                            }
                        </Grid>
                    </Grid>
                    <Button disableElevation
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        className={classes.submit}
                        disabled={loading}
                    >
                        Sign Up
                    </Button>
                    <Grid container justifyContent="flex-end">
                        <Grid item>
                            <Link variant="body2">
                                <UnstyledLink to={AppRouteURI.LOGIN}>
                                    Already have an account? Sign in
                                </UnstyledLink>
                            </Link>
                        </Grid>
                    </Grid>
                </form>
            </div>
            <Box mt={5}>
                <CopyrightFooter />
            </Box>
        </Container>
    );
}